// OB-21 — the portal credit dashboard. Customer-side mirror of OB-18's trader screen: one
// company's own credit line(s), month-major, read-only. No mutating affordance exists here, by
// design (§6.4) — the only action on a row is opening the drill-down.
//
// TERMINOLOGY (§7.1, corrected 2026-09-22): the cap is called "credit line" here, converging with
// the trader surface and the two customer-facing strings that already say it
// (ReservationPaymentPage.json's CreditLineErrorTitle / CreditLineBlockedContent). The selector,
// where shown at all, is "Invoice name" — a DELIBERATE divergence from the trader's "payment
// entity", matching what this reader already sees at checkout (NameOnInvoice).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Box,
	Chip,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	CircularProgress,
	Typography,
	Paper,
	Stack,
	Button,
	Alert,
	ButtonBase,
	IconButton,
	LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Ajax from '../../../utils/Ajax';
import ProfileStore from '../../../stores/ProfileStore';
import DocumentTitle from '../../general/DocumentTitle.react';
import CreditLineDrilldown from './CreditLineDrilldown.react';
import {
	cellBadge,
	cellCopy,
	classifyCell,
	blockCopyKey,
	closedCopyKey,
	modeBadge,
	modeBodyKey,
	sharedBannerKey,
	bucketMonthKey,
	defaultMonthWindowStart,
	formatMoneyAmount,
	headroomFigure,
	isDrillable,
	mayShowFigures,
	monthStripRange,
	utilizationBarValue,
	utilizationPctToken,
	CELL_KIND,
	CELL_TONE,
} from './creditLineDisplay';

// A filter value no request has been made with yet — distinct from `null`, which IS a filter value
// ("all invoice names"). Only a sentinel can tell "nothing loaded" from "the unfiltered view is
// loaded", and the preselection below depends on that distinction to avoid a second, identical GET.
const NO_FILTER_LOADED = {};

const MONTH_WINDOW_SIZE = 4;

const MODE_TONE_COLOR = {
	monitoring: 'default',
	active: 'success',
	hold: 'warning',
};

// Colour is never the only carrier: every tone also has an icon and a word (`cellBadge`), so the
// state survives greyscale and colour blindness.
const TONE_ICON = {
	[CELL_TONE.OK]: CheckCircleOutlineIcon,
	[CELL_TONE.ALERT]: WarningAmberOutlinedIcon,
	[CELL_TONE.BLOCK]: BlockOutlinedIcon,
	[CELL_TONE.CLOSED]: LockOutlinedIcon,
	[CELL_TONE.UNKNOWN]: HelpOutlineIcon,
};

const TONE_COLOR = {
	[CELL_TONE.OK]: 'success.main',
	[CELL_TONE.ALERT]: 'warning.main',
	[CELL_TONE.BLOCK]: 'error.main',
	[CELL_TONE.CLOSED]: 'text.secondary',
	[CELL_TONE.UNKNOWN]: 'text.disabled',
};

const TONE_BORDER_COLOR = {
	[CELL_TONE.OK]: 'divider',
	[CELL_TONE.ALERT]: 'warning.main',
	[CELL_TONE.BLOCK]: 'error.main',
	[CELL_TONE.CLOSED]: 'divider',
	[CELL_TONE.UNKNOWN]: 'divider',
};

const TONE_PROGRESS_COLOR = {
	[CELL_TONE.OK]: 'success',
	[CELL_TONE.ALERT]: 'warning',
	[CELL_TONE.BLOCK]: 'error',
	[CELL_TONE.CLOSED]: 'inherit',
	[CELL_TONE.UNKNOWN]: 'inherit',
};

// Which instant the page calls "now". The server's `as_of` is the authority — it is the clock the
// bucket months were computed against. The client clock is a fallback for a payload with no usable
// timestamp: a stale month label beats refusing to render figures the server did send.
function payloadNow(as_of) {
	const parsed = as_of ? new Date(as_of) : null;

	return parsed && Number.isFinite(parsed.getTime()) ? parsed : new Date();
}

function StateBadge({ cell, row, size }) {
	const { t } = useTranslation();
	const badge = cellBadge(cell, row);
	const Icon = TONE_ICON[badge.tone];

	return (
		<Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: TONE_COLOR[badge.tone] }}>
			<Icon fontSize={size === 'small' ? 'inherit' : 'small'} sx={{ flexShrink: 0 }} />
			<Typography variant={size === 'small' ? 'caption' : 'body2'} sx={{ fontWeight: 600 }}>
				{t(badge.key)}
			</Typography>
		</Stack>
	);
}

function ModeBadge({ row }) {
	const { t } = useTranslation();
	const badge = modeBadge(row);

	return <Chip size="small" label={t(badge.key)} color={MODE_TONE_COLOR[badge.tone]} />;
}

function Money({ amount, currency }) {
	const { t } = useTranslation();

	if (!Number.isFinite(amount)) {
		return null;
	}

	return <>{t('CreditLinePage.Amount', { amount: formatMoneyAmount(amount), currency })}</>;
}

function HeroFigure({ labelKey, amount, currency }) {
	const { t } = useTranslation();

	if (!Number.isFinite(amount)) {
		return null;
	}

	return (
		<Box sx={{ minWidth: 140 }}>
			<Typography variant="caption" color="text.secondary" display="block">
				{t(labelKey)}
			</Typography>
			<Typography variant="h6">
				<Money amount={amount} currency={currency} />
			</Typography>
		</Box>
	);
}

/**
 * The current month, in full. Every honesty state reaches this too: an UNKNOWN or unreconstructible
 * month renders its sentence and NO figures and NO bar (§3.3) rather than a confident "0 of 0 · 0%".
 */
function CurrentMonthHero({ row, month, cell, onOpen }) {
	const { t } = useTranslation();
	const kind = classifyCell(cell, row);
	const badge = cellBadge(cell, row);
	const copy = cellCopy(cell, row);
	const bar_value = utilizationBarValue(cell, row);
	const clickable = isDrillable(cell, row);
	const headroom = headroomFigure(cell);
	// §4 — a BLOCK month's sentence is the row's mode; a CLOSED month is NOT "fully used" and gets
	// its own, so the sentence can never contradict the badge above it.
	const enforcement_copy_key =
		kind === CELL_KIND.BLOCK ? blockCopyKey(row) : kind === CELL_KIND.CLOSED ? closedCopyKey(row) : null;

	return (
		<Box
			sx={{
				p: 2,
				mb: 2,
				borderRadius: 1,
				border: '1px solid',
				borderColor: TONE_BORDER_COLOR[badge.tone],
				bgcolor: 'action.hover',
			}}
		>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
				<Typography variant="subtitle2" color="text.secondary">
					{t('CreditLinePage.ThisMonth', { month })}
				</Typography>
				<StateBadge cell={cell} row={row} />
			</Stack>

			{mayShowFigures(cell, row) ? (
				<>
					<Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1.5 }}>
						<HeroFigure labelKey="CreditLinePage.HeroCap" amount={cell.cap} currency={row.currency} />
						<HeroFigure labelKey="CreditLinePage.HeroUsed" amount={cell.used} currency={row.currency} />
						{headroom && (
							<HeroFigure labelKey={headroom.labelKey} amount={headroom.amount} currency={row.currency} />
						)}
					</Stack>

					{bar_value !== null && (
						<>
							<LinearProgress
								variant="determinate"
								value={bar_value}
								color={TONE_PROGRESS_COLOR[badge.tone]}
								aria-label={t('CreditLinePage.UtilizationBar')}
								sx={{ height: 8, borderRadius: 1 }}
							/>
							<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
								{t('CreditLinePage.PercentUsed', {
									utilization_pct: utilizationPctToken(cell.utilization_pct),
								})}
							</Typography>
						</>
					)}
				</>
			) : (
				<Typography variant="body2" color="text.secondary">
					{t(copy.key, copy.values)}
				</Typography>
			)}

			{enforcement_copy_key && (
				<Typography variant="body2" sx={{ mt: 1.5 }}>
					{t(enforcement_copy_key)}
				</Typography>
			)}

			{clickable && (
				<Button size="small" sx={{ mt: 1, px: 0 }} onClick={onOpen}>
					{t('CreditLinePage.SeeReservations')}
				</Button>
			)}
		</Box>
	);
}

function MonthCell({ row, month, cell, onOpen }) {
	const { t } = useTranslation();
	const copy = cellCopy(cell, row);
	const kind = classifyCell(cell, row);
	const badge = cellBadge(cell, row);
	const bar_value = utilizationBarValue(cell, row);
	const clickable = isDrillable(cell, row);

	return (
		<ButtonBase
			disabled={!clickable}
			onClick={clickable ? onOpen : undefined}
			sx={{
				display: 'block',
				textAlign: 'start',
				flex: '1 1 0',
				minWidth: 0,
				p: 1.5,
				borderRadius: 1,
				border: '1px solid',
				borderColor: TONE_BORDER_COLOR[badge.tone],
				bgcolor: 'background.paper',
				color: 'text.primary',
			}}
		>
			<Typography variant="caption" color="text.secondary" display="block">
				{month}
			</Typography>

			<Box sx={{ my: 0.5 }}>
				<StateBadge cell={cell} row={row} size="small" />
			</Box>

			<Typography variant="body2">{t(copy.key, copy.values)}</Typography>

			{bar_value !== null && (
				<LinearProgress
					variant="determinate"
					value={bar_value}
					color={TONE_PROGRESS_COLOR[badge.tone]}
					aria-label={t('CreditLinePage.UtilizationBar')}
					sx={{ height: 6, borderRadius: 1, mt: 1 }}
				/>
			)}

			{kind === CELL_KIND.BLOCK && (
				<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
					{t(blockCopyKey(row))}
				</Typography>
			)}
		</ButtonBase>
	);
}

function CreditLineRow({ row, asOf }) {
	const { t } = useTranslation();
	const [drilldownMonth, setDrilldownMonth] = useState(null);
	const [drilldown, setDrilldown] = useState(null);
	const [drilldownLoading, setDrilldownLoading] = useState(false);
	const banner_key = sharedBannerKey(row);
	// The server's own instant decides which bucket is "this month" — read in UTC by
	// `bucketMonthKey`, exactly as the gate buckets — so the hero can never label a bucket the gate
	// is not charging.
	const now = useMemo(() => payloadNow(asOf), [asOf]);
	const months = useMemo(() => monthStripRange(row.months, now), [row.months, now]);
	const current_month = bucketMonthKey(now);
	const has_current_month = months.includes(current_month);

	// §3.2 — the strip is a bounded window over the axis the server already returned, never a
	// re-fetch: paging is free, and there is nothing to switch to that wasn't already loaded.
	const defaultStart = useMemo(() => defaultMonthWindowStart(months, MONTH_WINDOW_SIZE, now), [months, now]);
	const [windowStart, setWindowStart] = useState(defaultStart);
	// A refresh can return a SHORTER axis than the one the window was positioned against; clamping on
	// read keeps `visible_months` non-empty rather than stranding the strip past the end.
	const maxStart = Math.max(0, months.length - MONTH_WINDOW_SIZE);
	const start = Math.min(windowStart, maxStart);
	const visible_months = months.slice(start, start + MONTH_WINDOW_SIZE);
	const can_go_back = start > 0;
	const can_go_forward = start + MONTH_WINDOW_SIZE < months.length;

	const loadDrilldownPage = useCallback(
		(month, page) => {
			setDrilldownMonth(month);
			setDrilldownLoading(true);

			// `getCreditLineMonthDrilldown` returns a jQuery Deferred: `.always`, NEVER `.finally`
			// (which does not exist on it and throws).
			Ajax.getCreditLineMonthDrilldown(row.credit_line_id, month, page)
				.then((result) => setDrilldown(result))
				.catch(() => setDrilldown(null))
				.always(() => setDrilldownLoading(false));
		},
		[row.credit_line_id],
	);

	const openMonth = useCallback(
		(month) => {
			setDrilldown(null);
			loadDrilldownPage(month, 1);
		},
		[loadDrilldownPage],
	);

	// Paging keeps the month fixed: only the page moves, so the dialog never reopens on a
	// different bucket than the one the customer clicked.
	const goToDrilldownPage = useCallback(
		(page) => loadDrilldownPage(drilldownMonth, page),
		[loadDrilldownPage, drilldownMonth],
	);

	const closeDrilldown = useCallback(() => {
		setDrilldownMonth(null);
		setDrilldown(null);
	}, []);

	return (
		<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
				<ModeBadge row={row} />
				{banner_key && (
					<Typography variant="body2" color="text.secondary">
						{t(banner_key)}
					</Typography>
				)}
			</Stack>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				{t(modeBodyKey(row))}
			</Typography>

			{row.status === 'unknown' ? (
				// §2.5/§8.2 OQ-5 — UNKNOWN is not zero and not an empty strip; say so, don't imply
				// there is nothing to measure.
				<Typography variant="body2" color="text.secondary">
					{t('CreditLinePage.NotMeasuredYet')}
				</Typography>
			) : (
				<>
					{has_current_month && (
						<CurrentMonthHero
							row={row}
							month={current_month}
							cell={row.by_month[current_month]}
							onOpen={() => openMonth(current_month)}
						/>
					)}

					<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
						{t('CreditLinePage.MonthByMonth')}
					</Typography>

					<Stack direction="row" spacing={1} alignItems="center">
						<IconButton
							size="small"
							aria-label={t('CreditLinePage.EarlierMonths')}
							disabled={!can_go_back}
							onClick={() => setWindowStart(Math.max(0, start - MONTH_WINDOW_SIZE))}
						>
							<ChevronLeftIcon />
						</IconButton>

						<Stack direction="row" spacing={1.5} sx={{ overflow: 'hidden', flex: 1 }}>
							{visible_months.map((month) => (
								<MonthCell
									key={month}
									row={row}
									month={month}
									cell={row.by_month[month]}
									onOpen={() => openMonth(month)}
								/>
							))}
						</Stack>

						<IconButton
							size="small"
							aria-label={t('CreditLinePage.LaterMonths')}
							disabled={!can_go_forward}
							onClick={() => setWindowStart(Math.min(maxStart, start + MONTH_WINDOW_SIZE))}
						>
							<ChevronRightIcon />
						</IconButton>
					</Stack>
				</>
			)}

			<CreditLineDrilldown
				open={Boolean(drilldownMonth)}
				onClose={closeDrilldown}
				month={drilldownMonth}
				loading={drilldownLoading}
				drilldown={drilldown}
				onPageChange={goToDrilldownPage}
			/>
		</Paper>
	);
}

// The company's own payment entities, read from the session profile — `company.invoice_ids` is
// `company.icount_ids` verbatim (`routes/private/global.js`), the SAME list the checkout invoice
// dropdown offers, so the two surfaces can never disagree about which entities exist.
//
// Deliberately NOT derived from the response's rows, which is what this filter used to do and why
// it did not work: (a) a response already narrowed to one entity carries one identity, so the
// option list collapsed to a single item the moment you used it and there was no way back to
// another entity or to "all"; (b) an entity with NO credit line attached produces no row at all,
// so it was invisible rather than answerable — and "this entity has no credit line" is precisely
// the statement a customer needs; (c) `row.identities` holds icount IDs, which the old list
// rendered raw as the option's label.
function readInvoiceNames() {
	const profile = ProfileStore.getProfile();
	const entries = (profile && profile.company && profile.company.invoice_ids) || [];

	return entries
		.filter((entry) => entry && entry.id)
		.map((entry) => ({ id: entry.id, name: entry.name || entry.id }));
}

function useInvoiceNames() {
	const [invoiceNames, setInvoiceNames] = useState(readInvoiceNames);

	useEffect(() => {
		const onProfileChange = () => setInvoiceNames(readInvoiceNames());

		ProfileStore.addChangeListener(onProfileChange);
		return () => ProfileStore.removeChangeListener(onProfileChange);
	}, []);

	return invoiceNames;
}

// Which of the company's own invoice names are charged to a given row. The server answers this per
// row in `identities` — the requesting company's identities that RESOLVE to that credit line
// (`@arbitrip/credit-line/credit_line_portal`) — so this is a lookup, never a second derivation.
function invoiceNamesOnRow(row, invoiceNames) {
	const identities = (row && row.identities) || [];

	return invoiceNames.filter((entry) => identities.includes(entry.id));
}

function InvoiceNameFilter({ invoiceNames, value, onChange }) {
	const { t } = useTranslation();

	// §3.1 — omitted entirely for the common case of one payment entity: a control for a choice
	// nobody has to make is a control that trains the reader on a noun they never needed.
	if (invoiceNames.length < 2) {
		return null;
	}

	return (
		<FormControl size="small" sx={{ minWidth: 220 }}>
			<InputLabel id="credit-line-invoice-name-label">{t('CreditLinePage.InvoiceName')}</InputLabel>
			<Select
				labelId="credit-line-invoice-name-label"
				id="credit-line-invoice-name"
				label={t('CreditLinePage.InvoiceName')}
				value={value || ''}
				onChange={(event) => onChange(event.target.value || null)}
			>
				{/* Not a sum: "all" renders every credit line as its OWN row, exactly as the server
				    returns them. Caps of different payment entities are not additive and are never
				    added up here. */}
				<MenuItem value="">{t('CreditLinePage.AllInvoiceNames')}</MenuItem>
				{invoiceNames.map(({ id, name }) => (
					<MenuItem key={id} value={id}>
						{name}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}

const CreditLinePage = () => {
	const { t } = useTranslation();
	const [state, setState] = useState({ loading: true, error: false, payload: null });
	const [icountId, setIcountId] = useState(null);
	const invoiceNames = useInvoiceNames();
	// Which filter the payload currently on screen was fetched with. The preselection below adopts
	// a selection the CURRENT payload already satisfies, and this is what stops that from firing a
	// second request for a row set we are already rendering.
	const loadedFilterRef = useRef(NO_FILTER_LOADED);
	// A preselection may never overrule a person: once the reader has used the selector, their
	// choice — including "all invoice names" — is the answer and the page keeps it.
	const readerChoseRef = useRef(false);

	const load = useCallback((filter) => {
		loadedFilterRef.current = filter;
		setState((prev) => ({ ...prev, loading: true, error: false }));

		Ajax.getCreditLineMonthly(filter)
			.then((payload) => setState({ loading: false, error: false, payload }))
			.catch(() => setState({ loading: false, error: true, payload: null }));
	}, []);

	useEffect(() => {
		if (loadedFilterRef.current !== icountId) {
			load(icountId);
		}
	}, [icountId, load]);

	const chooseInvoiceName = useCallback((value) => {
		readerChoseRef.current = true;
		setIcountId(value);
	}, []);

	// Memoised so the effect below keys off the PAYLOAD changing, not off every render.
	const rows = useMemo(() => (state.payload ? state.payload.rows : []), [state.payload]);

	// BUG 34 — the control must name what the page is showing. "All invoice names" beside a page
	// that is in fact showing ONE entity's credit line is the dropdown contradicting the figures,
	// and on a money surface that reads as "these numbers cover everything" when they do not.
	// So: when the company resolves to exactly one credit line and exactly one invoice name is
	// charged to it, preselect that name — the control then says precisely what is on screen.
	//
	// Adopting it does NOT re-fetch. Filtering by an identity of the only row returns that same
	// row: the server SELECTS rows by identity and never slices a row's figures
	// (`@arbitrip/credit-line/credit_line_portal`), so the filtered payload is the one already loaded.
	useEffect(() => {
		if (readerChoseRef.current || icountId || state.loading || state.error) {
			return;
		}

		if (invoiceNames.length < 2 || rows.length !== 1) {
			return;
		}

		const covered = invoiceNamesOnRow(rows[0], invoiceNames);

		// Two or more invoice names on the one credit line: naming a single one of them would be
		// the same contradiction in the other direction, since the figures are the shared line's.
		// "All invoice names" is the honest label there, explained by the note below it.
		if (covered.length !== 1) {
			return;
		}

		loadedFilterRef.current = covered[0].id;
		setIcountId(covered[0].id);
	}, [state, rows, icountId, invoiceNames]);

	// The other half of the same honesty problem: several invoice names, ONE credit line behind
	// them. Selecting any of them genuinely shows the same figures, and without a sentence saying
	// so the selector looks broken. Named explicitly rather than implied.
	const shared_invoice_names = useMemo(() => {
		if (invoiceNames.length < 2 || rows.length !== 1) {
			return [];
		}

		const covered = invoiceNamesOnRow(rows[0], invoiceNames);

		return covered.length > 1 ? covered.map((entry) => entry.name) : [];
	}, [invoiceNames, rows]);

	const selected_invoice_name = useMemo(() => {
		const match = invoiceNames.find((entry) => entry.id === icountId);

		return match ? match.name : icountId;
	}, [invoiceNames, icountId]);

	return (
		<DocumentTitle title={t('CreditLinePage.Title')}>
			<Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
				<Typography variant="h4" gutterBottom>
					{t('CreditLinePage.Title')}
				</Typography>

				<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
					<Button
						size="small"
						variant="outlined"
						startIcon={<RefreshIcon fontSize="small" />}
						onClick={() => load(icountId)}
					>
						{t('CreditLinePage.Refresh')}
					</Button>

					<InvoiceNameFilter invoiceNames={invoiceNames} value={icountId} onChange={chooseInvoiceName} />

					{state.payload && (
						<Typography variant="body2" color="text.secondary">
							{t('CreditLinePage.AsOf', { time: new Date(state.payload.as_of).toLocaleString() })}
						</Typography>
					)}
				</Stack>

				{/* Several invoice names, one credit line behind them: say so, or picking a name
				    looks like a control that does nothing. */}
				{shared_invoice_names.length > 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						{t('CreditLinePage.OneCreditLineForInvoiceNames', {
							invoice_names: shared_invoice_names.join(', '),
						})}
					</Typography>
				)}

				{state.loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				)}

				{!state.loading && state.error && <Alert severity="error">{t('CreditLinePage.LoadError')}</Alert>}

				{/* No row for the selection is "there is NO credit line here", never "the credit line
				    is 0" — two entirely different statements about money. It gets its own sentence,
				    naming the entity the reader picked, and no figures of any kind. */}
				{!state.loading && !state.error && rows.length === 0 && (
					<Typography variant="body1" color="text.secondary">
						{icountId
							? t('CreditLinePage.NoCreditLineForInvoiceName', { invoice_name: selected_invoice_name })
							: t('CreditLinePage.NoCreditLine')}
					</Typography>
				)}

				{/* The selection is part of the key: switching payment entity REMOUNTS the row, which
				    is what guarantees an open drill-down from the previous entity cannot survive the
				    switch and show the wrong entity's reservations. */}
				{!state.loading &&
					!state.error &&
					rows.map((row) => (
						<CreditLineRow
							key={`${icountId || 'all'}:${row.credit_line_id}`}
							row={row}
							asOf={state.payload.as_of}
						/>
					))}
			</Box>
		</DocumentTitle>
	);
};

export default CreditLinePage;
